import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import type { InputHTMLAttributes } from 'react';
import { useState } from 'react';

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  field: {
    state: {
      value: string | undefined;
      meta: {
        errors: unknown[];
        isValidating: boolean;
      };
    };
    handleChange: (value: string) => void;
    handleBlur: () => void;
  };
  label?: string;
  labelClassName?: string;
  inputClassName?: string;
  showErrors?: boolean;
}

function getValidationErrorMessage(error: unknown) {
  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = Reflect.get(error, 'message');
    return typeof message === 'string' ? message : undefined;
  }

  return undefined;
}

export default function FormInput({
  field,
  label,
  className = '',
  labelClassName,
  inputClassName,
  showErrors = true,
  ...props
}: FormInputProps) {
  const errors = field.state.meta.errors
    .map(getValidationErrorMessage)
    .filter((error): error is string => Boolean(error));
  const hasError = showErrors && errors.length > 0;
  const isValidating = field.state.meta.isValidating;
  const isPassword = props.type === 'password';
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={props.id}
          className={`block text-sm font-medium ${labelClassName || 'text-foreground'}`}
        >
          {label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          {...props}
          type={isPassword && showPassword ? 'text' : props.type}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange(e.target.value)}
          onBlur={field.handleBlur}
          className={`
            block w-full h-10 px-3 
            border rounded-lg 
            focus:outline-none focus:ring-2 focus:ring-offset-0
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
            ${!inputClassName?.includes('bg-') ? 'bg-background' : ''}
            ${!inputClassName?.includes('text-') ? 'text-foreground' : ''}
            ${!inputClassName?.includes('placeholder:') ? 'placeholder:text-muted-foreground' : ''}
            ${
              hasError
                ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500/20'
                : !inputClassName?.includes('border-')
                  ? 'border-border focus:border-primary focus:ring-primary/20'
                  : ''
            }
            ${isValidating || isPassword ? 'pr-10' : ''}
            ${inputClassName || ''}
            ${className}
          `}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
        {isValidating && !isPassword && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          </div>
        )}
        {hasError && !isValidating && !isPassword && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <AlertCircle className="h-4 w-4 text-red-500" />
          </div>
        )}
      </div>
      {hasError && <p className="text-xs text-red-500 flex items-center gap-1">{errors[0]}</p>}
    </div>
  );
}
